<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import GroupCreate from '@/components/group/group-create.vue';
import ItemList from '@/components/menu/common/item-list.vue';
import SectionWrapper from '@/components/menu/sections/section-wrapper.vue';
import { sortActiveGroups } from '@/utils/sorters';
import {
  useGroups,
  useGroupUnreadActivityCounts,
  useUserInfo,
} from '@/utils/swr';

type ItemListProps = InstanceType<typeof ItemList>['$props'];
type ItemsProps = ItemListProps['items'];

const route = useRoute();
const { data: userInfo } = useUserInfo();
const { data: groups } = useGroups();
const { data: groupUnreadActivityCounts } = useGroupUnreadActivityCounts();

const memberGroups = computed(() => {
  if (groups.value) {
    return sortActiveGroups(groups.value.groups, 'name', 'asc').filter(
      group => group.is_member,
    );
  }
  return [];
});
const isCreateGroup = computed(() => {
  return ['group_admin', 'admin'].includes(userInfo.value?.role ?? '');
});

const isGroupCreateOpen = ref(false);

const items = computed(() => {
  let items: ItemsProps = [
    {
      id: 'group_search',
      name: 'グループ検索',
      route: { name: 'groupList' },
      icon: 'search',
      isSelectedExactRoute: true,
    },
  ];
  if (isCreateGroup.value) {
    items.push({
      id: 'group_create',
      name: 'グループ作成',
      onClick: () => (isGroupCreateOpen.value = true),
      icon: 'plus',
    });
  }
  return items;
});

const getUnreadCount = (groupId: number) =>
  groupUnreadActivityCounts.value?.unread_activity_counts[String(groupId)] ?? 0;

const groupItems = computed(
  () =>
    memberGroups.value.map(g => ({
      id: g.id,
      name: g.name,
      route: {
        name: 'groupActivity',
        params: { groupId: String(g.id) },
      },
      icon: 'users',
      count: getUnreadCount(g.id),
      onClick: () => {
        if (groupUnreadActivityCounts.value && getUnreadCount(g.id)) {
          groupUnreadActivityCounts.value.unread_activity_counts[String(g.id)] =
            0;
        }
      },
      isSelected:
        route.meta.type === 'group' && route.params.groupId === String(g.id),
    })) as ItemsProps,
);
</script>

<template>
  <SectionWrapper>
    <ItemList :items="items" />
    <ItemList
      title="参加済み"
      :items="groupItems"
      :key="`groups-${groupItems.length}`"
    />
    <GroupCreate v-model:open="isGroupCreateOpen" />
  </SectionWrapper>
</template>
<style lang="scss" scoped></style>
